export type LabBudgetExceededError = {
  code: "budget_exceeded";
  limitUsd: number | null;
  currentUsd: number | null;
  reservedUsd: number | null;
  committedUsd: number | null;
  requestedUsd: number | null;
  remainingUsd: number | null;
  currency: string;
  configKey: string | null;
};

const BUDGET_ERROR_CODES = new Set([
  "budget_exceeded",
  "labs_budget_exceeded",
  "spend_budget_exceeded",
]);

const LIMIT_FIELDS = [
  "max_spend_usd",
  "budget_limit_usd",
  "limit_usd",
  "max_budget_usd",
];
const CURRENT_FIELDS = [
  "current_usd",
  "spent_usd",
  "current_spend_usd",
  "used_usd",
];
const RESERVED_FIELDS = ["reserved_usd", "pending_reservations_usd"];
const REQUESTED_FIELDS = [
  "requested_usd",
  "estimated_cost_usd",
  "reservation_usd",
];

export function labBudgetExceeded(
  error: unknown,
): LabBudgetExceededError | null {
  const record = asRecord(error);
  if (!record) return null;
  const payload = asRecord(record.payload);
  const nestedError = asRecord(payload?.error);
  const details =
    asRecord(record.details) ??
    asRecord(payload?.details) ??
    asRecord(nestedError?.details);
  const code = firstString([
    record.code,
    record.error_code,
    record.reason_code,
    payload?.code,
    payload?.error_code,
    payload?.reason_code,
    nestedError?.code,
    nestedError?.error_code,
    nestedError?.reason_code,
    typeof payload?.error === "string" ? payload.error : null,
  ])?.toLowerCase();

  if (!code || !BUDGET_ERROR_CODES.has(code)) return null;
  const sources = [details, nestedError, payload, record];
  const currentUsd = numberFrom(sources, CURRENT_FIELDS);
  const reservedUsd = numberFrom(sources, RESERVED_FIELDS);
  return {
    code: "budget_exceeded",
    limitUsd: numberFrom(sources, LIMIT_FIELDS),
    currentUsd,
    reservedUsd,
    committedUsd:
      currentUsd == null && reservedUsd == null
        ? null
        : (currentUsd ?? 0) + (reservedUsd ?? 0),
    requestedUsd: numberFrom(sources, REQUESTED_FIELDS),
    remainingUsd: numberFrom(sources, ["remaining_usd"]),
    currency: stringFrom(sources, ["currency"]) ?? "USD",
    configKey: stringFrom(sources, ["config_key"]),
  };
}

export function formatUsd(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function stringFrom(
  sources: Array<Record<string, unknown> | null>,
  fields: string[],
) {
  for (const source of sources) {
    for (const field of fields) {
      const value = source?.[field];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }
  return null;
}

function numberFrom(
  sources: Array<Record<string, unknown> | null>,
  fields: string[],
) {
  for (const source of sources) {
    for (const field of fields) {
      const value = source?.[field];
      if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
        return value;
      }
      if (typeof value === "string" && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed >= 0) return parsed;
      }
    }
  }
  return null;
}

function firstString(values: unknown[]) {
  return values.find(
    (value): value is string => typeof value === "string" && Boolean(value),
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}
